import "./styles/discover.css";
import { cloneElement, isValidElement, useEffect, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent, type ReactElement } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { DiscoverRail, defaultRailHref } from "./components/discover-rail";
import { DiscoverMain } from "./components/discover-main";
import { DetailSlideOver } from "./components/detail-slide-over";
import {
  ConceptACompactMap,
  DrilldownList,
  countsState,
} from "./components/discover-mobile-compact";
import { DiscoverMobileTrips } from "./components/discover-mobile-trips";
import { TripPlanner } from "./components/trip-planner/trip-planner";
import { useLikedItems } from "./hooks/use-liked-items";
import { useHeaderTakeover } from "./hooks/use-header-takeover";
import { TripProvider } from "./state/trip-context";
import {
  DiscoverProvider,
  useDiscoverActions,
  useDiscoverContext,
} from "./state/discover-context";
import { DiscoverConfigProvider, useDiscoverConfig } from "./config";
import type { DiscoverConfig } from "./config";
import type { DiscoverItem, UtilityMode } from "./types";

import { Icon } from "@majourneys/icons";
function findItemByUrlKey(
  items: readonly DiscoverItem[],
  key: string,
): DiscoverItem | undefined {
  return items.find((i) => i.sourceSlug === key) ?? items.find((i) => i.id === key);
}

/**
 * The trip planner on its own page, at `/{lang}/trip-planner`.
 *
 * Same component the Discover rail shows, with only the providers it actually
 * needs: `TripProvider` for the trips themselves, and `DiscoverProvider` +
 * config underneath because adding a stop searches the catalogue and the day
 * map reads cities from it. No rail, no map column, no URL sync — the page
 * around it owns those.
 */
export function DiscoverTripPlanner({ config }: { config: DiscoverConfig }) {
  return (
    <DiscoverConfigProvider config={config}>
      <DiscoverProvider initialMode="trips">
        <TripProvider>
          <TripPlanner />
        </TripProvider>
      </DiscoverProvider>
    </DiscoverConfigProvider>
  );
}

export function DiscoverApp({
  config,
  initialMode,
}: {
  config: DiscoverConfig;
  /**
   * The section the URL asked for. Each rail mode has its own path now
   * (`/discover/chat`, `/discover/trips`, …), so the route hands the mode in
   * rather than the app sniffing a query param out of `window.location` — the
   * old `?utility=` read, which only ran on the client and only after mount.
   */
  initialMode?: UtilityMode;
}) {
  return (
    <DiscoverConfigProvider config={config}>
      <DiscoverProvider initialMode={initialMode}>
        <TripProvider>
          <DiscoverUrlSync initialMode={initialMode} />
          <section className="mj-discover-page">
            <div className="mj-discover-shell mj-discover-shell--desktop">
              <DiscoverRail />
              <DiscoverMain />
            </div>
            <div className="mj-discover-shell--mobile">
              <DiscoverMobileCompact />
              <div className="mj-discover-mobile-rail">
                <DiscoverRail />
              </div>
              <DiscoverMobileDetail />
            </div>
          </section>
        </TripProvider>
      </DiscoverProvider>
    </DiscoverConfigProvider>
  );
}

function DiscoverMobileCompact() {
  const { state, items, cities, isLoadingItems, itemsError } = useDiscoverContext();
  const { selectItem } = useDiscoverActions();
  // The mobile bottom rail switches utilityMode; each mode gets its own
  // mobile surface so no rail tab is a dead no-op.
  if (state.utilityMode === "trips") return <DiscoverMobileTrips />;
  if (state.utilityMode === "liked") return <DiscoverMobileLiked />;
  if (state.utilityMode === "chat") return <DiscoverMobileChat />;
  return (
    <ConceptACompactMap
      items={items}
      cities={cities}
      onSelectItem={(id) => selectItem(id)}
      counts={countsState(items.length, isLoadingItems, itemsError)}
    />
  );
}

function DiscoverMobileLiked() {
  const { t } = useTranslation();
  const { state } = useDiscoverContext();
  const { selectItem, toggleLiked } = useDiscoverActions();
  const likedItems = useLikedItems();
  // Liked is a full-screen panel over the map, so on a phone it has the
  // screen the same way a full sheet does: the site header steps away.
  useHeaderTakeover(true);
  return (
    <div className="mj-mobile-liked">
      <header className="mj-mobile-liked__head">
        <h2>{t("discover.liked_picks")}</h2>
        {/* The empty hint is said once, in the panel below, not here as well. */}
        {likedItems.length > 0 ? <p>{t("discover.liked_count", { count: likedItems.length })}</p> : null}
      </header>
      {likedItems.length === 0 ? (
        <div className="mj-mobile-liked__empty">
          <Icon name="ri-heart-3-line" aria-hidden="true" />
          <p>{t("discover.liked_empty_hint")}</p>
        </div>
      ) : (
        <DrilldownList
          items={likedItems}
          liked={state.likedIds}
          onToggleLike={toggleLiked}
          onSelectItem={(id) => selectItem(id)}
        />
      )}
    </div>
  );
}

/** What the phone gives the chat: the full-screen layout and a way back. */
export type DiscoverPhoneChat = { onBack: () => void };

/** Set on <html> while the phone chat has the whole screen; the rail hides. */
const CHAT_FULL_ATTR = "data-mj-discover-chat";

/**
 * The AI chat on a phone takes the whole screen (owner, 2026-10-12, option C
 * of the phone chat lab): the site header and the tab rail step aside, the
 * chat's own header carries a back arrow to Explore, and the message box sits
 * at the very bottom. It used to be a panel between the two, and the box ended
 * up under the rail where nobody could see it.
 */
function DiscoverMobileChat() {
  const { features, railHref } = useDiscoverConfig();
  const { setUtilityMode } = useDiscoverActions();
  const { i18n } = useTranslation();
  useHeaderTakeover(true);
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute(CHAT_FULL_ATTR, "full");
    return () => root.removeAttribute(CHAT_FULL_ATTR);
  }, []);
  const onBack = () => {
    setUtilityMode("explore");
    const lang = i18n.language?.slice(0, 2) || "en";
    window.history.replaceState(null, "", (railHref ?? defaultRailHref)("explore", lang));
  };
  const chat = isValidElement(features?.aiChat)
    ? cloneElement(features.aiChat as ReactElement<{ phone?: DiscoverPhoneChat }>, { phone: { onBack } })
    : features?.aiChat;
  return <div className="mj-mobile-chat">{chat}</div>;
}

function DiscoverMobileDetail() {
  const { state, items } = useDiscoverContext();
  const { closeDetail } = useDiscoverActions();
  const open = state.isDetailOpen;
  const item = open
    ? items.find((i) => i.id === state.selectedItemId) ?? null
    : null;
  // A quick look, so a sheet (owner, 2026-11-03, option B): it opens at
  // half height over the map or the trip, pulls up for the full details,
  // and pulls down or a tap outside puts the guest back where they were.
  const [snap, setSnap] = useState<"half" | "full">("half");
  const [drag, setDrag] = useState(0);
  const startY = useRef<number | null>(null);
  const itemId = item?.id;
  useEffect(() => setSnap("half"), [itemId]);
  if (!open || !item) return null;
  // On the page body, not in the phone shell: inside it the panel could not
  // rise above the site header, which covered its close and Book buttons,
  // and opened from a trip it left the trip's header showing above it.
  if (typeof document === "undefined") return null;

  // Pointer events cover touch and mouse alike. The handle always drags;
  // at half height the whole sheet does (its body does not scroll yet); at
  // full height only the handle, so reading never moves the sheet.
  const onDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    const onGrab = Boolean((e.target as HTMLElement).closest(".gd-grab"));
    if (!onGrab && snap === "full") return;
    if ((e.target as HTMLElement).closest("a, button, input")) return;
    startY.current = e.clientY;
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (startY.current === null) return;
    const dy = e.clientY - startY.current;
    setDrag(snap === "full" ? Math.max(0, dy) : dy);
  };
  const onUp = () => {
    if (startY.current === null) return;
    startY.current = null;
    const dy = drag;
    setDrag(0);
    if (snap === "half" && dy < -40) setSnap("full");
    else if (snap === "half" && dy > 80) closeDetail();
    else if (snap === "full" && dy > 80) setSnap("half");
    // A tap on the handle toggles.
    else if (Math.abs(dy) < 4) setSnap((v) => (v === "half" ? "full" : "half"));
  };

  return createPortal(
    <div
      className={`mj-discover-mobile-detail is-${snap}${drag !== 0 ? " is-dragging" : ""}`}
      style={{ "--mj-sheet-drag": `${drag}px` } as CSSProperties}
    >
      <div className="mj-discover-mobile-detail__scrim" onClick={closeDetail} aria-hidden="true" />
      <div
        className="mj-discover-mobile-detail__sheet"
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
      >
        <DetailSlideOver item={item} open={open} />
      </div>
    </div>,
    document.body,
  );
}

function DiscoverUrlSync({ initialMode }: { initialMode?: UtilityMode }) {
  const { state, items } = useDiscoverContext();
  const { selectItem, closeDetail, setUtilityMode } = useDiscoverActions();
  const hydrated = useRef(false);
  const pendingKey = useRef<string | null>(null);

  // `?utility=` only. The route's own mode is seeded into the reducer by
  // `DiscoverProvider`, because setting it here — after the first paint —
  // showed Explore for a beat on every load of `/ai-chat`, and put Explore
  // in the server's HTML besides.
  useEffect(() => {
    if (initialMode) return;
    if (typeof window === "undefined") return;
    const utility = new URLSearchParams(window.location.search).get("utility");
    const allowed: UtilityMode[] = ["explore", "cities", "trips", "liked", "chat"];
    if (utility && (allowed as string[]).includes(utility)) {
      setUtilityMode(utility as UtilityMode);
    }
  }, [initialMode, setUtilityMode]);

  // Capture the initial item key once on mount; resolve when items arrive.
  useEffect(() => {
    if (typeof window === "undefined") return;
    pendingKey.current = new URLSearchParams(window.location.search).get("item");
  }, []);

  // Resolve pending URL key against loaded items.
  useEffect(() => {
    if (items.length === 0) return;
    const key = pendingKey.current;
    if (key) {
      const match = findItemByUrlKey(items, key);
      if (match) selectItem(match.id);
      pendingKey.current = null;
    }
    hydrated.current = true;
  }, [items, selectItem]);

  // Back/forward sync.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onPop = () => {
      const params = new URLSearchParams(window.location.search);
      const key = params.get("item");
      if (key) {
        const match = findItemByUrlKey(items, key);
        if (match) selectItem(match.id);
      } else {
        closeDetail();
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [items, selectItem, closeDetail]);

  // Reflect selection into URL using sourceSlug (fallback to id).
  // Preserves scroll position: TanStack Router's scrollRestoration intercepts
  // history changes and resets the page scroll, even on replaceState.
  // Snapshot scroll before the URL update and restore it on the next frame.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!hydrated.current) return;
    const url = new URL(window.location.href);
    const current = url.searchParams.get("item");
    let next: string | null = null;
    if (state.isDetailOpen && state.selectedItemId) {
      const selected = items.find((i) => i.id === state.selectedItemId);
      next = selected?.sourceSlug || state.selectedItemId;
    }
    if (current === next) return;
    if (next) url.searchParams.set("item", next);
    else url.searchParams.delete("item");
    const prevX = window.scrollX;
    const prevY = window.scrollY;
    window.history.replaceState(null, "", url.toString());
    // Restore on the next two frames — TanStack scrollRestoration runs in a
    // post-commit effect, so a single rAF isn't always enough.
    requestAnimationFrame(() => {
      window.scrollTo({ left: prevX, top: prevY, behavior: "auto" });
      requestAnimationFrame(() =>
        window.scrollTo({ left: prevX, top: prevY, behavior: "auto" }),
      );
    });
  }, [state.isDetailOpen, state.selectedItemId, items]);

  return null;
}
